import { makeStyles } from '@material-ui/core'

const drawerWidth = 240

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    position: 'relative'
  },
  appBar: {
    zIndex: theme.zIndex.drawer + 1,
    backgroundColor: theme.palette.background.default,
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen
    })
  },
  appBarShift: {
    // Logical property, not `marginLeft`, deliberately. The whole app is wrapped in a
    // global jss-rtl StylesProvider (components/RTL) that flips physical left/right
    // properties on EVERY JSS rule it sees, including MUI's own internal Drawer
    // styles -- which already picks the correct RTL-aware anchor side on its own via
    // theme.direction. That double-flip put this margin and MUI's own anchor
    // disagreeing about which side the drawer opens on. `marginInlineStart` isn't one
    // of jss-rtl's flip targets, so it renders identically regardless of direction and
    // sidesteps the double-flip entirely, matching the actual rendered side the
    // browser resolves from the ambient `dir` attribute.
    marginInlineStart: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen
    })
  },
  menuButton: {
    marginRight: 36
  },
  hide: {
    display: 'none'
  },
  topMenu: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
    paddingRight: 60
  },
  topMenuContainer: {
    backgroundColor: theme.palette.background.paper,
    '& svg': {
      color: theme.palette.grey[700],
      '&:hover': {
        color: theme.palette.primary.main,
        backgroundColor: 'transparent'
      }
    }
  },
  leftTopMenu: { display: 'flex', alignItems: 'center', position: 'relative' },
  rightTopMenu: { display: 'flex', flexDirection: 'row', position: 'relative' },
  profileImage: {
    width: '100%',
    height: '100%'
  },
  iconButton: {
    color: theme.palette.grey[700],
    fontSize: 60,
    margin: '0 2px',
    position: 'relative',
    transition: '300ms',

    '&:hover': {
      color: theme.palette.primary.main,
      backgroundColor: 'transparent'
    }
  },
  shoppingCartIcon: {
    animation: '$shake 0.2s'
  },
  '@keyframes shake': {
    '0%': {
      transform: 'rotate(-7deg)'
    },
    '50%': {
      transform: 'rotate(7deg)'
    },
    '100%': {
      transform: 'rotate(-7deg)'
    }
  },
  rotateIcon: {},
  jobCounter: {
    fontSize: 15,
    color: theme.palette.primary.contrastText,
    backgroundColor: theme.palette.primary.main,
    width: 20,
    height: 20,
    borderRadius: '50%',
    textAlign: 'center',
    verticalAlign: 'center',
    position: 'absolute',
    bottom: 6,
    right: 6
  },
  tableContainer: {
    position: 'absolute',
    width: 800,
    right: 50,
    top: 50,
    marginRight: -26
  },
  jobsstyle: {
    position: 'absolute',
    width: 800,
    right: 50,
    top: 50,
    marginRight: 142
  },
  profileOpened: {
    transform: 'rotate(180deg)',
    transition: '300ms',
    margin: 0
  },
  profileNotOpened: { transition: '300ms', margin: 0 },
  profileImageContainer: {
    width: 90,
    height: 45,
    [theme.breakpoints.down('sm')]: {
      width: 50
    }
  },
  itticImageContainer: {
    width: 100,
    height: 64,
    marginRight: -24,
    [theme.breakpoints.down('sm')]: {
      width: 50
    }
  },
  homaImageContainer: {
    width: 80
  },
  profileMenuContainer: {
    backgroundColor: theme.palette.background.default,
    padding: `2px 10px 0 5px`,
    borderRadius: 5,
    boxShadow: `0 0 2px ${theme.palette.grey[400]} `,
    position: 'absolute',
    top: 40,
    right: -145
  },
  langContainer: {
    backgroundColor: theme.palette.background.default,
    padding: '2px 10px 0 5px',
    borderRadius: 5,
    boxShadow: `0 0 2px ${theme.palette.grey[400]} `,
    position: 'absolute',
    // Anchored to the BOTTOM OF THE BUTTON, not by a fixed offset.
    //
    // This was `bottom: -90`, which pins the panel's bottom edge 90px below the button
    // and lets its top edge land wherever the content height puts it — so the panel
    // MOVED depending on how many languages the signed-in user has. With one language
    // it floated far below the globe with a visible gap; with three it would ride up
    // and overlap the button. Whoever chose -90 was looking at one particular list.
    //
    // `top: 100%` puts the top edge at the button's bottom edge whatever the height,
    // which is what the jobs dropdown in this same header does with `top: 50`.
    // Deliberately no zIndex: this panel is a descendant of the AppBar, so it sits in
    // the AppBar's own stacking context and is already above page content and the
    // drawer. A local value here can only order it against the header's OTHER popups,
    // and `theme.zIndex.appBar + 1` (1101) reads as if it lifts the panel above the
    // app bar while being lower than the AppBar's own 1201 — misleading and inert.
    // The jobs dropdown in this same header sets none and renders correctly.
    top: '100%',
    right: 0,
    marginTop: 4
  },
  ListItemlang: {},
  drawer: {
    width: drawerWidth,
    flexShrink: 0,
    whiteSpace: 'nowrap',
    height: '100%',
    [theme.breakpoints.down('sm')]: {
      zIndex: -1
    }
  },
  drawerOpen: {
    width: drawerWidth,
    [theme.breakpoints.down('sm')]: {
      position: 'absolute',
      // Logical property -- see the comment on appBarShift above.
      insetInlineStart: 0
    },
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen
    }),
    [theme.breakpoints.down('sm')]: {
      zIndex: theme.zIndex.drawer + 1,
      visibility: 'visible !important'
    }
  },
  list: {
    '& svg': {
      color: theme.palette.grey[700]
    },
    '& span': {
      color: theme.palette.grey[700]
    }
  },
  listPadding: {
    padding: '5px 0px'
  },
  drawerClose: {
    // Logical property -- see the comment on appBarShift above.
    insetInlineStart: 0,
    backgroundColor: theme.palette.background.paper,
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen
    }),
    overflowX: 'hidden',
    overflowY: 'auto',
    width: theme.spacing(7) + 1,
    [theme.breakpoints.down('sm')]: {
      width: theme.spacing(9) + 1,
      position: 'absolute'
    }
  },
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    padding: theme.spacing(0, 1),

    ...theme.mixins.toolbar
  },
  content: {
    flexGrow: 1,
    padding: `${theme.spacing(3)}px ${theme.spacing(12)}px`,
    backgroundColor: theme.palette.background.default,
    minHeight: '100vh',
    [theme.breakpoints.down('sm')]: {
      padding: `${theme.spacing(3)}px ${theme.spacing(2)}px`,
      width: 'calc(100% - 73px)'
    }
  },
  activeListItem: {
    borderRight: `3px solid ${theme.palette.primary.main}`,
    '& span': { color: theme.palette.primary.main },
    '& svg': { color: theme.palette.primary.main },
    backgroundColor: theme.palette.grey[200]
  },
  ListItemName: { '& span': { color: theme.palette.primary.main } },
  ListItemLogout: {
    padding: '2px 40px 0 0',

    '&:hover': {
      backgroundColor: 'transparent',
      color: theme.palette.primary.light
    }
  },
  ListItemIconLogout: { minWidth: 20 },
  iranSans: {
    '& p': { fontFamily: 'IranSans' }
  },
  mainCountdown: {
    fontSize: '0.75rem',
    color: theme.palette.primary.main,
    paddingTop: '2.125rem'
  },
  containerCountdown: {
    fontSize: '0.75rem',
    color: theme.palette.grey[600],
    paddingTop: '2.125rem'
  }
}))

export default useStyles
